import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { apiClient, ApiError } from "@/api/client";
import type { SubjectSummary } from "@/api/types";

export const useCatalogueStore = defineStore("catalogue", () => {
  const subjects = ref<SubjectSummary[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const filters = ref<Record<string, string>>({});
  const taxonomy = ref<Record<string, string[]>>({});
  const enrolledSubjectIds = ref<Set<string>>(new Set());

  const byId = computed(() => {
    const map: Record<string, SubjectSummary> = {};
    for (const subject of subjects.value) {
      map[subject.id] = subject;
    }
    return map;
  });

  function isEnrolled(subjectId: string): boolean {
    return enrolledSubjectIds.value.has(subjectId);
  }

  async function loadTaxonomy(): Promise<void> {
    try {
      taxonomy.value = await apiClient.getTaxonomy();
    } catch {
      taxonomy.value = {};
    }
  }

  async function load(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      // Strip empty-string filter values so the URL stays clean.
      const activeFilters: Record<string, string> = {};
      for (const [k, v] of Object.entries(filters.value)) {
        if (v) activeFilters[k] = v;
      }
      subjects.value = await apiClient.listSubjects(
        Object.keys(activeFilters).length ? activeFilters : undefined,
      );
    } catch (e) {
      error.value =
        e instanceof ApiError
          ? `API error ${e.status}`
          : "Failed to load catalogue";
      subjects.value = [];
    } finally {
      loading.value = false;
    }
  }

  async function fetchEnrolledSubjects(): Promise<void> {
    try {
      const enrolled = await apiClient.getMySubjects();
      enrolledSubjectIds.value = new Set(enrolled.map((s) => s.subject_id));
    } catch {
      enrolledSubjectIds.value = new Set();
    }
  }

  function setFilter(key: string, value: string): void {
    if (value) {
      filters.value[key] = value;
    } else {
      delete filters.value[key];
    }
  }

  function clearFilters(): void {
    filters.value = {};
  }

  return {
    subjects,
    loading,
    error,
    filters,
    taxonomy,
    enrolledSubjectIds,
    byId,
    isEnrolled,
    loadTaxonomy,
    load,
    fetchEnrolledSubjects,
    setFilter,
    clearFilters,
  };
});
